/**
 * Der Punkt in der Kopfzeile: laeuft der Belegeingang noch?
 *
 * „Stille Ausfaelle sind der Feind" ist Regel 5 der Ablaeufe. Wenn das
 * Postfach seit zwei Tagen nicht gelesen wurde, sieht der Eingang genauso aus
 * wie an einem Tag ohne Post — leer. Der Unterschied steht in
 * `GET /health/ingest`, und deshalb steht er hier oben, wo er auf jeder Seite
 * ist.
 *
 * Die Route antwortet absichtlich mit **503**, wenn nichts frisch ist (§15.1
 * S6). Der Client behandelt das nicht als Fehler, sondern als Antwort — siehe
 * `validateStatus` in `lib/api.ts`.
 *
 * Neben dem Sammelpunkt steht das Alter **je Quelle** (Sammelpostfach,
 * Sweeper, Bank). Der Grund ist derselbe wie fuer den Punkt ueberhaupt: Der
 * Sammelpunkt wird gruen, sobald irgendeine Quelle frisch ist. Der Sweeper
 * laeuft seltener und liefert seltener etwas — faellt er aus, faellt das
 * niemandem auf. Deshalb hat jede Quelle ihre eigene Zahl.
 */

import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';

import { bh } from '@/lib/api';
import { alterText, datumZeit } from '@/lib/format';
import { quelleLabel } from '@/lib/beleg';
import type { QuelleStand } from '@/types';

export function StatusPunkt() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['health-ingest'],
    queryFn: () => bh.ingestHealth(),
    // Alle fuenf Minuten nachsehen. Der Job selbst laeuft alle fuenf Minuten;
    // haeufiger zu fragen erzeugt Last ohne neue Auskunft.
    refetchInterval: 5 * 60 * 1000,
    retry: false,
  });

  if (isLoading) {
    return <Punkt farbe="grau" text="Status wird geprüft …" />;
  }

  // Nicht erreichbar ist etwas anderes als „nicht frisch" — und beides ist
  // etwas anderes als „alles gut". Wer daraus zwei Zustaende macht, meldet
  // beim naechsten Netzhaenger einen Ausfall des Belegeingangs.
  if (isError || !data) {
    return <Punkt farbe="grau" text="Status nicht erreichbar" />;
  }

  const alter = alterText(data.ingest_alter_stunden);
  const stumm = data.quellen.filter((q) => !q.ok);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      {data.ok ? (
        <Punkt farbe="gruen" text={`Belegeingang läuft — letzter Lauf ${alter}`} />
      ) : (
        <Punkt
          farbe="rot"
          text={`Belegeingang stockt — letzter Lauf ${alter}`}
          titel={
            data.hinweis ??
            (stumm.length
              ? `Ohne frischen Lauf: ${stumm.map((q) => quelleLabel(q.quelle)).join(', ')}`
              : undefined)
          }
        />
      )}

      {/* Je Quelle das Alter des letzten Laufs.
          Der Sammelpunkt allein reicht nicht: Er wird gruen, solange
          *irgendetwas* frisch ist. Wenn das Sammelpostfach im Minutentakt
          laeuft und der Sweeper seit drei Tagen schweigt, sieht ein einzelner
          Punkt gut aus — und genau der Sweeper ist der, dessen Ausfall
          niemandem auffaellt, weil sein Ergebnis ohnehin selten ist. */}
      {data.quellen.map((quelle) => (
        <QuellePunkt key={quelle.quelle} quelle={quelle} />
      ))}
    </div>
  );
}

function QuellePunkt({ quelle }: { quelle: QuelleStand }) {
  const nieGelaufen = quelle.alter_stunden === null || quelle.alter_stunden === undefined;
  const titel = [
    `Letzter Lauf: ${datumZeit(quelle.letzter_lauf_at)}`,
    `Letzter Erfolg: ${datumZeit(quelle.letzter_erfolg_at)}`,
    quelle.fehllaeufe_in_folge > 0
      ? `${quelle.fehllaeufe_in_folge} Fehlläufe in Folge`
      : 'keine Fehlläufe',
  ].join(' · ');

  return (
    <span
      title={titel}
      className={clsx(
        'inline-flex items-center gap-1.5 text-xs',
        quelle.ok ? 'text-slate-400' : 'font-medium text-danger-700',
      )}
    >
      <span
        className={clsx(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          !quelle.ok ? 'bg-danger-500' : nieGelaufen ? 'bg-slate-300' : 'bg-success-500',
        )}
      />
      {quelleLabel(quelle.quelle)} {alterText(quelle.alter_stunden)}
    </span>
  );
}

function Punkt({
  farbe,
  text,
  titel,
}: {
  farbe: 'gruen' | 'rot' | 'grau';
  text: string;
  titel?: string;
}) {
  return (
    <span
      title={titel}
      className={clsx(
        'inline-flex items-center gap-2 text-xs',
        farbe === 'rot' ? 'font-medium text-danger-700' : 'text-slate-500',
      )}
    >
      <span
        className={clsx(
          'h-2 w-2 shrink-0 rounded-full',
          farbe === 'gruen' && 'bg-success-500',
          farbe === 'rot' && 'bg-danger-500',
          farbe === 'grau' && 'bg-slate-300',
        )}
      />
      {text}
    </span>
  );
}
